import { AppShell, Avatar, Badge, Burger, Divider, Group, Menu, NavLink, ScrollArea, Text, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import {
  IconBook, IconChartBar, IconChecklist, IconChevronDown, IconHistory, IconLayoutDashboard, IconListDetails, IconLock,
  IconLogout, IconSchool, IconSettings, IconShieldCheck, IconUserPlus, IconUsers,
} from '@tabler/icons-react';
import { Link as RouterLink, Outlet, useLocation } from 'react-router';
import { logout } from '../lib/auth';
import { useMe } from '../lib/me';
import type { AdminRole } from '../lib/types';
import { IncidentLink } from './IncidentLink';

interface NavItem { to: string; label: string; icon: typeof IconSchool; exact?: boolean; roles?: AdminRole[] }

const employeeNav: NavItem[] = [
  { to: '/', label: 'Мои курсы', icon: IconSchool, exact: true },
  { to: '/history', label: 'История обучения', icon: IconHistory },
  { to: '/profile', label: 'Профиль', icon: IconSettings },
];
const adminNav: NavItem[] = [
  { to: '/admin', label: 'Сводка', icon: IconLayoutDashboard, exact: true, roles: ['ASSIGNMENTS', 'RESULTS', 'CONTENT'] },
  { to: '/admin/content', label: 'Контент: курсы', icon: IconBook, roles: ['CONTENT'] },
  { to: '/admin/approvals', label: 'Контент: одобрение', icon: IconChecklist, roles: ['CONTENT'] },
  { to: '/admin/assignments', label: 'Назначения', icon: IconUserPlus, roles: ['ASSIGNMENTS'] },
  { to: '/admin/results', label: 'Результаты', icon: IconChartBar, roles: ['RESULTS'] },
  { to: '/admin/users', label: 'Пользователи и роли', icon: IconUsers, roles: ['ASSIGNMENTS', 'RESULTS'] },
  { to: '/admin/audit', label: 'Журнал аудита', icon: IconListDetails, roles: ['RESULTS'] },
];

export function Layout() {
  const me = useMe();
  const [opened, { toggle, close }] = useDisclosure();
  const { pathname } = useLocation();
  const isActive = (to: string, exact?: boolean) => (exact ? pathname === to : pathname.startsWith(to));
  const visibleAdmin = adminNav.filter((i) => i.roles!.some((r) => me.roles.includes(r)));

  const links = (items: NavItem[]) =>
    items.map((i) => (
      <NavLink key={i.to} component={RouterLink} to={i.to} label={i.label} leftSection={<i.icon size={18} stroke={1.6} />} active={isActive(i.to, i.exact)} onClick={close} style={{ borderRadius: 8 }} />
    ));

  return (
    <AppShell header={{ height: 60 }} navbar={{ width: 250, breakpoint: 'sm', collapsed: { mobile: !opened } }} padding="md">
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between" wrap="nowrap">
          <Group gap="sm" wrap="nowrap">
            <Burger opened={opened} onClick={toggle} hiddenFrom="sm" size="sm" />
            <IconShieldCheck size={28} color="var(--mantine-color-blue-6)" />
            <div>
              <Text fw={700} lh={1.1}>Chiron</Text>
              <Text size="xs" c="dimmed" lh={1.1} visibleFrom="xs">Обучение кибербезопасности</Text>
            </div>
          </Group>
          <Group gap="sm" wrap="nowrap">
            <IncidentLink compact />
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <UnstyledButton>
                  <Group gap="xs" wrap="nowrap">
                    <Avatar color="blue" radius="xl" size="sm">{me.fullName.split(' ').map((p) => p[0]).join('').slice(0, 2)}</Avatar>
                    <div style={{ lineHeight: 1.1 }}>
                      <Text size="sm" fw={500} visibleFrom="sm">{me.fullName}</Text>
                      <Text size="xs" c="dimmed" visibleFrom="sm">{me.department ?? 'Без подразделения'}</Text>
                    </div>
                    <IconChevronDown size={14} />
                  </Group>
                </UnstyledButton>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Label>{me.email}</Menu.Label>
                <Menu.Item component={RouterLink} to="/profile" leftSection={<IconSettings size={16} />}>Профиль</Menu.Item>
                <Menu.Item leftSection={<IconLogout size={16} />} onClick={() => logout()}>Выйти</Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Navbar p="sm">
        <ScrollArea>
          <Text size="xs" fw={600} c="dimmed" tt="uppercase" px="sm" mb={4}>Обучение</Text>
          {links(employeeNav)}
          {visibleAdmin.length > 0 && (
            <>
              <Divider my="sm" />
              <Group px="sm" mb={4} gap={6}>
                <Text size="xs" fw={600} c="dimmed" tt="uppercase">Команда обучения</Text>
                <Badge size="xs" variant="light" leftSection={<IconLock size={10} />}>2FA</Badge>
              </Group>
              <Group px="sm" mb={6} gap={4}>{me.roleLabels.map((r) => <Badge key={r} size="xs" variant="outline">{r}</Badge>)}</Group>
              {links(visibleAdmin)}
            </>
          )}
        </ScrollArea>
      </AppShell.Navbar>

      <AppShell.Main>
        <div style={{ maxWidth: 1180, margin: '0 auto' }}>
          <Outlet />
        </div>
      </AppShell.Main>
    </AppShell>
  );
}
